import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mic,
  Volume2,
  Sparkles,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  ThumbsUp,
  Target,
  Plus,
  Trash2,
  Upload,
  Play,
  Settings,
  Loader2,
  Radio,
  Brain,
  Network,
  RefreshCw,
  AudioLines,
  TrendingUp,
  Building2,
  Users,
  UserCircle,
  FileText,
  Link,
  Clock,
  HelpCircle,
  X,
  Square,
  CircleDot,
  ListChecks,
  Pencil,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Persona configurations
const PERSONA_CONFIG = {
  "max-returns": {
    name: "Max Returns",
    role: "AI Investor",
    color: "from-cyan-500 to-purple-500",
    image: "/max_returns.png",
    icon: TrendingUp,
    description: "Seasoned VC who evaluates your pitch from an investor perspective",
    quickPrompts: [
      { icon: Sparkles, label: "Witty reply", type: "witty_reply" },
      { icon: Lightbulb, label: "Success story", type: "success_story" },
      { icon: AlertTriangle, label: "Cautionary tale", type: "cautionary_tale" },
      { icon: Target, label: "Recommendations", type: "final_recommendations" },
      { icon: ThumbsUp, label: "Positive feedback", type: "positive_feedback" },
      { icon: MessageSquare, label: "Constructive", type: "constructive_feedback" },
      { icon: Network, label: "Network intros", type: "network_intro" },
      { icon: Brain, label: "Key questions", type: "key_questions" },
    ]
  },
  "diana-pipeline": {
    name: "Diana Pipeline",
    role: "AI Pharma Director",
    color: "from-emerald-500 to-teal-500",
    image: "/diana_pipeline.png",
    icon: Building2,
    description: "Head of External Innovation evaluating strategic fit and partnership potential",
    quickPrompts: [
      { icon: Target, label: "Strategic fit", type: "strategic_fit" },
      { icon: Brain, label: "Development path", type: "development_path" },
      { icon: Lightbulb, label: "Partnership terms", type: "partnership_terms" },
      { icon: AlertTriangle, label: "Regulatory concerns", type: "regulatory_concerns" },
      { icon: ThumbsUp, label: "Positive signal", type: "positive_feedback" },
      { icon: MessageSquare, label: "Deeper dive", type: "deeper_dive" },
      { icon: Network, label: "Internal champions", type: "internal_champions" },
      { icon: Sparkles, label: "Next steps", type: "next_steps" },
    ]
  },
  "marco-moderator": {
    name: "Sergej Panél",
    role: "AI Panel Moderator",
    color: "from-amber-500 to-orange-500",
    image: "/marco_moderator.png",
    icon: Users,
    description: "The charismatic host who facilitates discussions and manages Q&A",
    quickPrompts: [
      { icon: Sparkles, label: "Introduce speaker", type: "introduce_speaker" },
      { icon: MessageSquare, label: "Open Q&A", type: "open_qa" },
      { icon: Target, label: "Time check", type: "time_check" },
      { icon: Lightbulb, label: "Summarize", type: "summarize" },
      { icon: ThumbsUp, label: "Thank speaker", type: "thank_speaker" },
      { icon: AlertTriangle, label: "Redirect", type: "redirect" },
      { icon: Brain, label: "Probe deeper", type: "probe_deeper" },
      { icon: Network, label: "Connect themes", type: "connect_themes" },
    ]
  },
  "alex-attendee": {
    name: "Alex Chen",
    role: "AI Attendee",
    color: "from-rose-500 to-pink-500",
    image: "/alex_chen.png",
    icon: UserCircle,
    description: "A curious attendee - fellow founder, scientist, or industry observer",
    quickPrompts: [
      { icon: Brain, label: "Ask question", type: "ask_question" },
      { icon: Sparkles, label: "Share insight", type: "share_insight" },
      { icon: Lightbulb, label: "Make connection", type: "make_connection" },
      { icon: MessageSquare, label: "Small talk", type: "small_talk" },
      { icon: ThumbsUp, label: "Show interest", type: "show_interest" },
      { icon: Target, label: "Exchange cards", type: "exchange_cards" },
      { icon: Network, label: "Introduce self", type: "introduce_self" },
      { icon: AlertTriangle, label: "Challenge idea", type: "challenge_idea" },
    ]
  }
};

// WebRTC Audio Chat class
class RealtimeAudioChat {
  constructor(onMessage, onConnectionChange) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.mediaStream = null;
    this.onMessage = onMessage;
    this.onConnectionChange = onConnectionChange;
  }

  async init(voice = null) {
    try {
      this.onConnectionChange?.("connecting");
      
      // Step 1: Get ephemeral token from backend (pass voice if provided)
      console.log("[WebRTC] Fetching ephemeral token...");
      const tokenRes = await fetch(`${BACKEND_URL}/api/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: voice ? JSON.stringify({ voice }) : JSON.stringify({})
      });
      
      const tokenData = await tokenRes.json();
      
      if (!tokenRes.ok) {
        console.error("[WebRTC] Token fetch failed:", tokenData);
        throw new Error(tokenData.detail || "Failed to get session token");
      }
      
      const ephemeralToken = tokenData.value;
      console.log("[WebRTC] Got ephemeral token:", ephemeralToken?.substring(0, 20) + "...");
      
      if (!ephemeralToken) {
        throw new Error("No ephemeral token received from server");
      }
      
      // Step 2: Create peer connection
      console.log("[WebRTC] Setting up peer connection...");
      this.peerConnection = new RTCPeerConnection();
      
      // Set up audio element for playback
      this.audioElement = document.createElement("audio");
      this.audioElement.autoplay = true;
      this.peerConnection.ontrack = (e) => {
        this.audioElement.srcObject = e.streams[0];
      };

      // Step 3: Get user microphone
      console.log("[WebRTC] Requesting microphone access...");
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.peerConnection.addTrack(this.mediaStream.getTracks()[0]);
        console.log("[WebRTC] Microphone access granted");
      } catch (micErr) {
        console.error("Microphone error:", micErr);
        throw new Error("Microphone access denied. Please allow microphone access and try again.");
      }

      // Create data channel
      this.dataChannel = this.peerConnection.createDataChannel("oai-events");
      this.dataChannel.addEventListener("message", (e) => {
        try {
          // Some messages might not be JSON (like SDP-related messages)
          if (typeof e.data === 'string' && e.data.startsWith('{')) {
            const msg = JSON.parse(e.data);
            this.onMessage?.(msg);
          }
        } catch (err) {
          // Silently ignore non-JSON messages
          console.debug("Non-JSON message received:", e.data?.substring?.(0, 50));
        }
      });

      // Step 4: Create and set local description
      console.log("[WebRTC] Creating offer...");
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Step 5: Send to OpenAI Realtime API
      console.log("[WebRTC] Sending offer to OpenAI...");
      const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${ephemeralToken}`,
          "Content-Type": "application/sdp"
        },
        body: offer.sdp
      });

      if (!sdpRes.ok) {
        const errorText = await sdpRes.text();
        console.error("[WebRTC] OpenAI error:", sdpRes.status, errorText);
        throw new Error(`OpenAI connection failed (${sdpRes.status}): ${errorText.substring(0, 100)}`);
      }

      const answerSdp = await sdpRes.text();
      console.log("[WebRTC] Got SDP answer, setting remote description...");
      
      await this.peerConnection.setRemoteDescription({
        type: "answer",
        sdp: answerSdp
      });

      this.onConnectionChange?.("connected");
    } catch (err) {
      console.error("Init error:", err);
      this.onConnectionChange?.("disconnected");
      throw err;
    }
  }

  sendMessage(msg) {
    if (this.dataChannel?.readyState === "open") {
      this.dataChannel.send(JSON.stringify(msg));
    }
  }

  setMicEnabled(enabled) {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  setSpeakerEnabled(enabled) {
    if (this.audioElement) {
      this.audioElement.muted = !enabled;
    }
  }

  disconnect() {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.peerConnection?.close();
    this.dataChannel?.close();
    this.onConnectionChange?.("disconnected");
  }
}

// Avatar Component
function PersonaAvatar({ persona, isListening, isSpeaking }) {
  const config = PERSONA_CONFIG[persona];
  return (
    <div className="relative">
      <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${config.color} overflow-hidden shadow-2xl ${isSpeaking ? "animate-pulse" : ""}`}>
        <img src={config.image} alt={config.name} className="w-full h-full object-cover" />
      </div>
      {isListening && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
          <div className="flex gap-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full bg-cyan-400 animate-bounce`} style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        </div>
      )}
      {isSpeaking && (
        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
          <Volume2 className="w-3 h-3 text-white animate-pulse" />
        </div>
      )}
    </div>
  );
}

// Quick Prompt Button
function QuickPromptButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled} className="bg-slate-800/50 border-slate-700 hover:bg-slate-700 text-slate-200">
      <Icon className="w-3 h-3 mr-1" /> {label}
    </Button>
  );
}

export default function AIPersonaPage() {
  const navigate = useNavigate();
  const { personaId } = useParams();
  const persona = PERSONA_CONFIG[personaId];
  
  // Redirect if invalid persona
  useEffect(() => {
    if (!persona) {
      navigate("/ai-personas");
    }
  }, [persona, navigate]);

  // Connection state
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [audioChat, setAudioChat] = useState(null);
  const [sessionMode, setSessionMode] = useState("listen_and_speak");
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivateBriefing, setShowPrivateBriefing] = useState(false);
  const [privateBriefing, setPrivateBriefing] = useState("Focus on understanding the commercial strategy, not just the science. Be encouraging but push for clarity on the business model and competitive differentiation.");
  
  // Knowledge base state
  const [newUrl, setNewUrl] = useState("");
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Question generation state (for listen mode)
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [questionInterval, setQuestionInterval] = useState(2); // minutes
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [conversationContext, setConversationContext] = useState("");
  const questionTimerRef = useRef(null);
  const transcriptRef = useRef("");
  
  // Extended Listening Mode state
  const [extendedListeningActive, setExtendedListeningActive] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [extendedQuestions, setExtendedQuestions] = useState([]);
  const [extendedTranscript, setExtendedTranscript] = useState(null); // Store transcript with speakers
  const [extendedTranscriptText, setExtendedTranscriptText] = useState(""); // Raw transcript text for context
  const [processingExtended, setProcessingExtended] = useState(false);
  const [speakingQuestion, setSpeakingQuestion] = useState(null);
  const [presenterName, setPresenterName] = useState(""); // Name of the presenter for personalized questions
  const [companyName, setCompanyName] = useState(""); // Company being pitched
  const [companyWebsite, setCompanyWebsite] = useState(""); // Website URL for additional context
  const [questionVoice, setQuestionVoice] = useState("echo"); // Voice for speaking questions (must work with both TTS and Realtime)
  const [askedQuestions, setAskedQuestions] = useState([]); // Track which questions have been asked
  const [previewingVoice, setPreviewingVoice] = useState(false); // Loading state for voice preview
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null); // Which question is being edited
  const [editingQuestionText, setEditingQuestionText] = useState(""); // Temp text while editing
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const voicePreviewRef = useRef(null); // Audio element for voice preview
  
  // Available voices that work with BOTH TTS and Realtime APIs
  // Realtime API supports: alloy, ash, ballad, coral, echo, sage, shimmer, verse, marin, cedar
  // TTS API supports: alloy, ash, coral, echo, fable, nova, onyx, sage, shimmer
  // Common voices: alloy, ash, coral, echo, sage, shimmer
  const VOICE_OPTIONS = [
    { id: "echo", name: "Echo", description: "Warm, resonant male" },
    { id: "alloy", name: "Alloy", description: "Neutral, balanced" },
    { id: "shimmer", name: "Shimmer", description: "Clear, bright female" },
    { id: "coral", name: "Coral", description: "Warm, friendly" },
    { id: "sage", name: "Sage", description: "Calm, thoughtful" },
    { id: "ash", name: "Ash", description: "Clear, precise" },
    { id: "verse", name: "Verse", description: "Versatile, expressive" },
    { id: "ballad", name: "Ballad", description: "Melodic, smooth" },
  ];
  
  // Preview the selected voice
  const previewVoice = async () => {
    // Stop any current preview
    if (voicePreviewRef.current) {
      voicePreviewRef.current.pause();
      voicePreviewRef.current = null;
    }
    
    setPreviewingVoice(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-persona/voice-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice: questionVoice })
      });
      
      if (!res.ok) throw new Error("Preview failed");
      
      const { audio } = await res.json();
      const audioEl = new Audio(audio);
      voicePreviewRef.current = audioEl;
      
      audioEl.onended = () => {
        setPreviewingVoice(false);
        voicePreviewRef.current = null;
      };
      
      audioEl.onerror = () => {
        setPreviewingVoice(false);
        voicePreviewRef.current = null;
        toast.error("Failed to play voice preview");
      };
      
      await audioEl.play();
    } catch (err) {
      console.error("Voice preview error:", err);
      toast.error("Failed to preview voice");
      setPreviewingVoice(false);
    }
  };
  
  // Check if persona supports extended listening (speaker diarization)
  const supportsExtendedListening = personaId === "max-returns" || personaId === "marco-moderator" || personaId === "alex-attendee";
  
  // Check if persona needs speaker diarization (AssemblyAI) vs simple transcription (Whisper)
  const needsSpeakerDiarization = personaId === "marco-moderator" || personaId === "alex-attendee";
  
  // Load settings
  useEffect(() => {
    if (personaId) {
      fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/settings`)
        .then(res => res.json())
        .then(data => setSettings(data))
        .catch(err => console.error("Failed to load settings:", err));
    }
  }, [personaId]);

  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    try {
      await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
    } catch (err) {
      console.error("Failed to save settings:", err);
    }
  };

  const updatePersonality = (key, value) => {
    const newPersonality = { ...(settings?.personality || {}), [key]: value };
    updateSettings({ personality: newPersonality });
  };

  const handleMessage = useCallback((msg) => {
    if (msg.type === "input_audio_buffer.speech_started") {
      setIsListening(true);
    } else if (msg.type === "input_audio_buffer.speech_stopped") {
      setIsListening(false);
    } else if (msg.type === "response.audio.delta") {
      setIsSpeaking(true);
    } else if (msg.type === "response.audio.done" || msg.type === "response.done") {
      setIsSpeaking(false);
    } else if (msg.type === "conversation.item.input_audio_transcription.completed") {
      // Capture transcribed speech for question generation
      const transcript = msg.transcript || "";
      if (transcript.trim()) {
        transcriptRef.current += " " + transcript;
        setConversationContext(prev => prev + " " + transcript);
      }
    }
  }, []);

  const handleConnectionChange = useCallback((status) => {
    setConnectionStatus(status);
  }, []);

  // Knowledge base functions
  const addKnowledgeUrl = async () => {
    if (!newUrl.trim()) return;
    const currentKb = settings?.knowledge_base || [];
    const newItem = { type: "url", content: newUrl.trim(), added_at: new Date().toISOString() };
    await updateSettings({ knowledge_base: [...currentKb, newItem] });
    setNewUrl("");
    toast.success("URL added to knowledge base");
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/knowledge/upload`, {
        method: "POST",
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const currentKb = settings?.knowledge_base || [];
        const newItem = { type: "document", name: file.name, content: data.extracted_text, added_at: new Date().toISOString() };
        await updateSettings({ knowledge_base: [...currentKb, newItem] });
        toast.success(`Document "${file.name}" added to knowledge base`);
      } else {
        toast.error("Failed to process document");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const removeKnowledgeItem = async (index) => {
    const currentKb = settings?.knowledge_base || [];
    const updated = currentKb.filter((_, i) => i !== index);
    await updateSettings({ knowledge_base: updated });
    toast.success("Item removed from knowledge base");
  };

  // Question generation for listen mode
  const generateQuestions = async () => {
    if (!conversationContext.trim() || generatingQuestions) return;
    
    setGeneratingQuestions(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/generate-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          conversation_context: conversationContext,
          knowledge_base: settings?.knowledge_base || []
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSuggestedQuestions(data.questions || []);
      }
    } catch (err) {
      console.error("Failed to generate questions:", err);
    } finally {
      setGeneratingQuestions(false);
    }
  };

  // Extended Listening Mode functions
  const startExtendedRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Collect data every second
      
      setIsRecording(true);
      setRecordingDuration(0);
      setExtendedQuestions([]);
      
      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      toast.success("Recording started - listening to the presentation");
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Failed to access microphone");
    }
  };

  // Track accumulated transcript from multiple generations
  const accumulatedTranscriptRef = useRef("");

  const stopExtendedRecording = async () => {
    if (!mediaRecorderRef.current || !isRecording) return null;
    
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
    
    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(audioBlob);
      };
      mediaRecorderRef.current.stop();
    });
  };

  // Get current audio without stopping recording
  const getCurrentAudio = async () => {
    if (!mediaRecorderRef.current || !isRecording) return null;
    
    return new Promise((resolve) => {
      // Stop current recorder to get audio
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioChunksRef.current = []; // Clear for next segment
        
        // Restart recording immediately
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const newRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
          
          newRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              audioChunksRef.current.push(e.data);
            }
          };
          
          newRecorder.start(1000);
          mediaRecorderRef.current = newRecorder;
        } catch (err) {
          console.error("Failed to restart recording:", err);
        }
        
        resolve(audioBlob);
      };
      
      mediaRecorderRef.current.stop();
    });
  };

  // Generate questions while continuing to record
  const generateExtendedQuestions = async (shouldStop = false) => {
    setProcessingExtended(true);
    
    try {
      // Get audio - either current segment or final
      const audioBlob = shouldStop 
        ? await stopExtendedRecording()
        : await getCurrentAudio();
      
      if (!audioBlob || audioBlob.size === 0) {
        toast.error("No audio recorded");
        setProcessingExtended(false);
        return;
      }
      
      // Use different transcription based on persona
      const useAssemblyAI = needsSpeakerDiarization;
      
      toast.info(shouldStop ? "Processing final segment..." : "Processing audio segment...");
      
      // Step 1: Transcribe audio
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      
      const transcribeEndpoint = useAssemblyAI 
        ? `${BACKEND_URL}/api/ai-persona/${personaId}/transcribe-audio`
        : `${BACKEND_URL}/api/ai-persona/${personaId}/transcribe-audio-simple`;
      
      const transcribeRes = await fetch(transcribeEndpoint, {
        method: "POST",
        body: formData
      });
      
      if (!transcribeRes.ok) {
        throw new Error("Transcription failed");
      }
      
      const transcriptData = await transcribeRes.json();
      
      // Handle different response formats
      let transcript, utterances, speakers, speaker_count;
      
      if (useAssemblyAI) {
        transcript = transcriptData.transcript;
        utterances = transcriptData.utterances;
        speakers = transcriptData.speakers;
        speaker_count = transcriptData.speaker_count;
        setExtendedTranscript(transcriptData);
        
        const speakerNames = Object.values(speakers || {}).filter(Boolean);
        if (speakerNames.length > 0) {
          toast.success(`Detected ${speaker_count} speakers: ${speakerNames.join(", ")}`);
        }
      } else {
        transcript = transcriptData.transcript;
        utterances = null;
        speakers = null;
        setExtendedTranscript(null);
      }
      
      if (!transcript || transcript.trim().length < 20) {
        toast.warning("Not enough new audio content");
        setProcessingExtended(false);
        return;
      }
      
      // Accumulate transcript
      accumulatedTranscriptRef.current += " " + transcript;
      setExtendedTranscriptText(accumulatedTranscriptRef.current);
      
      toast.info("Generating questions...");
      
      // Step 2: Generate questions from accumulated transcript
      const questionsRes = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/generate-extended-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transcript: accumulatedTranscriptRef.current,
          utterances: utterances || [],
          speakers: speakers || {},
          knowledge_base: settings?.knowledge_base || [],
          presenter_name: presenterName.trim() || null,
          company_name: companyName.trim() || null,
          company_website: companyWebsite.trim() || null
        })
      });
      
      if (!questionsRes.ok) {
        throw new Error("Question generation failed");
      }
      
      const { questions } = await questionsRes.json();
      
      // Append new questions to existing ones (avoid duplicates)
      setExtendedQuestions(prev => {
        const newQuestions = questions?.filter(q => !prev.includes(q)) || [];
        return [...prev, ...newQuestions];
      });
      
      const newCount = questions?.length || 0;
      toast.success(`Generated ${newCount} new questions${!shouldStop ? " (recording continues)" : ""}`);
      
    } catch (err) {
      console.error("Extended listening error:", err);
      toast.error(err.message || "Failed to process recording");
    } finally {
      setProcessingExtended(false);
    }
  };

  // Just stop recording without generating more questions
  const justStopRecording = async () => {
    await stopExtendedRecording();
    toast.info("Recording stopped. Select a question to ask.");
  };

  // Start editing a question
  const startEditingQuestion = (index) => {
    setEditingQuestionIndex(index);
    setEditingQuestionText(extendedQuestions[index]);
  };

  // Save edited question
  const saveEditedQuestion = () => {
    if (editingQuestionIndex !== null && editingQuestionText.trim()) {
      setExtendedQuestions(prev => {
        const updated = [...prev];
        updated[editingQuestionIndex] = editingQuestionText.trim();
        return updated;
      });
    }
    setEditingQuestionIndex(null);
    setEditingQuestionText("");
  };

  // Cancel editing
  const cancelEditingQuestion = () => {
    setEditingQuestionIndex(null);
    setEditingQuestionText("");
  };

  const speakQuestion = async (question, index) => {
    // Don't speak if currently editing
    if (editingQuestionIndex !== null) return;
    
    if (speakingQuestion !== null) {
      // Stop current playback
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
      setSpeakingQuestion(null);
      return;
    }
    
    // If still recording, just preview the question without starting follow-up
    const isPreviewOnly = isRecording;
    
    setSpeakingQuestion(index);
    
    // Track this question as asked (only if not just previewing)
    if (!isPreviewOnly) {
      setAskedQuestions(prev => [...prev, question]);
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/speak-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          voice: questionVoice || "echo"
        })
      });
      
      if (!res.ok) {
        throw new Error("TTS failed");
      }
      
      const { audio } = await res.json();
      
      // Play the audio
      const audioEl = new Audio(audio);
      audioPlayerRef.current = audioEl;
      
      audioEl.onended = async () => {
        setSpeakingQuestion(null);
        audioPlayerRef.current = null;
        
        // Only start follow-up session if not in preview mode (i.e., recording stopped)
        if (!isPreviewOnly) {
          toast.info("Listening for their answer...");
          await startFollowUpSession(question);
        } else {
          toast.info("Preview only - stop recording to ask questions");
        }
      };
      
      audioEl.onerror = () => {
        setSpeakingQuestion(null);
        audioPlayerRef.current = null;
        toast.error("Failed to play audio");
      };
      
      await audioEl.play();
      
    } catch (err) {
      console.error("Speak question error:", err);
      toast.error("Failed to speak question");
      setSpeakingQuestion(null);
    }
  };

  // Start a follow-up voice session after asking a question
  const startFollowUpSession = async (askedQuestion) => {
    try {
      const chat = new RealtimeAudioChat(handleMessage, handleConnectionChange);
      
      // Use the same voice selected for questions - pass it during init
      const selectedVoice = questionVoice || "echo";
      await chat.init(selectedVoice);
      
      if (chat.dataChannel) {
        const waitForChannel = () => new Promise(resolve => {
          if (chat.dataChannel.readyState === "open") resolve();
          else chat.dataChannel.addEventListener("open", resolve, { once: true });
        });
        await waitForChannel();
        
        const personaName = persona?.name || "the AI";
        
        // Build context from the Long Listen session
        let pitchContext = "";
        if (extendedTranscriptText) {
          // Summarize the transcript (take key parts)
          const transcriptSummary = extendedTranscriptText.length > 4000 
            ? extendedTranscriptText.slice(0, 4000) + "..."
            : extendedTranscriptText;
          pitchContext = `
=== PITCH CONTEXT ===
You listened to a presentation${presenterName ? ` by ${presenterName}` : ""}${companyName ? ` from ${companyName}` : ""}${companyWebsite ? ` (${companyWebsite})` : ""}.

Key points from their pitch:
${transcriptSummary}
=== END PITCH CONTEXT ===
`;
        }
        
        // Build list of questions already asked
        let questionsAskedContext = "";
        if (askedQuestions.length > 0) {
          questionsAskedContext = `
=== QUESTIONS YOU'VE ALREADY ASKED ===
${askedQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')}
=== END QUESTIONS ===

Remember: You've already asked these questions. Don't repeat them.
`;
        }
        
        // Set up the session to listen and respond with a brief closing comment
        const followUpPrompt = `You are ${personaName}, an investor listening to a founder's answer.

You just asked: "${askedQuestion}"

Now LISTEN to their answer. When they finish, say ONE short closing comment (5-10 words max).

Examples of good closing comments:
- "Thanks, that's really helpful."
- "Got it, appreciate the clarity."
- "Interesting, thanks for that."
- "Makes sense, thank you."
- "Great, that answers my question."

Keep it brief and warm. No follow-up questions.`;

        chat.sendMessage({
          type: "session.update",
          session: {
            instructions: followUpPrompt,
            voice: selectedVoice,
            modalities: ["text", "audio"],
            input_audio_transcription: { model: "whisper-1" },
            turn_detection: {
              type: "server_vad",
              threshold: 0.6,
              prefix_padding_ms: 400,
              silence_duration_ms: 1500
            }
          }
        });
      }
      
      setAudioChat(chat);
      setSessionMode("listen_and_speak");
      
    } catch (err) {
      console.error("Follow-up session error:", err);
      toast.error("Failed to start follow-up session");
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Start/stop question generation timer based on mode
  useEffect(() => {
    if (sessionMode === "only_listen" && connectionStatus === "connected" && questionInterval > 0) {
      // Start generating questions periodically
      questionTimerRef.current = setInterval(() => {
        generateQuestions();
      }, questionInterval * 60 * 1000); // Convert minutes to ms
      
      // Generate first set after 30 seconds
      const initialTimer = setTimeout(() => {
        generateQuestions();
      }, 30000);
      
      return () => {
        clearInterval(questionTimerRef.current);
        clearTimeout(initialTimer);
      };
    } else {
      // Clear timer if not in listen mode
      if (questionTimerRef.current) {
        clearInterval(questionTimerRef.current);
      }
    }
  }, [sessionMode, connectionStatus, questionInterval, conversationContext]);

  const startSession = async () => {
    try {
      // Fetch system prompt
      const promptRes = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/system-prompt`);
      let systemPrompt = "";
      if (promptRes.ok) {
        try {
          const promptData = await promptRes.json();
          systemPrompt = promptData.system_prompt;
        } catch (parseErr) {
          console.error("Failed to parse system prompt:", parseErr);
        }
      }
      
      // Add private briefing at the TOP if provided
      if (privateBriefing.trim()) {
        const briefingBlock = `
=======================================================
HIGHEST PRIORITY - PRIVATE INSTRUCTIONS FROM HOST
=======================================================
The host pulled you aside before this session and gave you these MANDATORY instructions. You MUST follow these throughout the entire session:

${privateBriefing}

These instructions override your default behavior. Follow them precisely.
=======================================================

`;
        systemPrompt = briefingBlock + systemPrompt;
      }
      
      const selectedVoice = settings?.voice || "verse";
      
      const chat = new RealtimeAudioChat(handleMessage, handleConnectionChange);
      await chat.init();
      
      if (chat.dataChannel && systemPrompt) {
        const waitForChannel = () => new Promise(resolve => {
          if (chat.dataChannel.readyState === "open") resolve();
          else chat.dataChannel.addEventListener("open", resolve, { once: true });
        });
        await waitForChannel();
        
        chat.sendMessage({
          type: "session.update",
          session: {
            instructions: systemPrompt,
            voice: selectedVoice,
            modalities: ["text", "audio"],
            input_audio_transcription: { model: "whisper-1" }
          }
        });
      }
      
      setAudioChat(chat);
      setShowPrivateBriefing(false);
    } catch (err) {
      console.error("Session error:", err);
      toast.error(err.message || "Failed to start voice session");
    }
  };

  const endSession = () => {
    audioChat?.disconnect();
    setAudioChat(null);
    setSessionMode("listen_and_speak");
    setIsListening(false);
    setIsSpeaking(false);
    // Clear question generation state
    setSuggestedQuestions([]);
    setConversationContext("");
    transcriptRef.current = "";
    if (questionTimerRef.current) {
      clearInterval(questionTimerRef.current);
    }
  };

  const setMode = (mode) => {
    if (!audioChat) return;
    setSessionMode(mode);
    
    const micOn = mode === "only_listen" || mode === "listen_and_speak";
    const speakerOn = mode === "only_speak" || mode === "listen_and_speak";
    
    setIsListening(micOn);
    audioChat.setMicEnabled(micOn);
    audioChat.setSpeakerEnabled(speakerOn);
  };

  const sendQuickPrompt = async (promptType) => {
    if (!audioChat || connectionStatus !== "connected") return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-persona/${personaId}/quick-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt_type: promptType })
      });
      
      if (res.ok) {
        const data = await res.json();
        audioChat.sendMessage({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "user",
            content: [{ type: "input_text", text: data.prompt }]
          }
        });
        audioChat.sendMessage({ type: "response.create" });
      }
    } catch (err) {
      console.error("Quick prompt error:", err);
    }
  };

  if (!persona) return null;

  const Icon = persona.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/ai-personas")} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-purple-400" />
            <span className="font-semibold">{persona.name}</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setShowSettings(true)}>
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Avatar & Status */}
        <div className="text-center mb-8">
          <PersonaAvatar 
            persona={personaId}
            isListening={isListening && (sessionMode === "only_listen" || sessionMode === "listen_and_speak")} 
            isSpeaking={isSpeaking && (sessionMode === "only_speak" || sessionMode === "listen_and_speak")} 
          />
          <h2 className="text-2xl font-bold mt-6 mb-1">{persona.name}</h2>
          <p className={`text-sm font-medium bg-gradient-to-r ${persona.color} bg-clip-text text-transparent mb-2`}>
            {persona.role}
          </p>
          <Badge className={`mt-2 ${connectionStatus === "connected" ? "bg-green-500/20 text-green-400" : connectionStatus === "connecting" ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-700 text-slate-400"}`}>
            <Radio className={`w-3 h-3 mr-1 ${connectionStatus === "connected" ? "animate-pulse" : ""}`} />
            {connectionStatus === "connected" ? "Connected" : connectionStatus === "connecting" ? "Connecting..." : "Disconnected"}
          </Badge>
        </div>

        {/* Controls */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex justify-center gap-4 mb-6">
              {connectionStatus !== "connected" ? (
                <Button onClick={() => setShowPrivateBriefing(true)} disabled={connectionStatus === "connecting"} className={`bg-gradient-to-r ${persona.color} px-8`}>
                  {connectionStatus === "connecting" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : <><Play className="w-4 h-4 mr-2" /> Start Session</>}
                </Button>
              ) : (
                <Button onClick={endSession} variant="outline" className="border-red-500/50 text-red-400">End Session</Button>
              )}
            </div>

            {/* Mode Selection */}
            {connectionStatus === "connected" && (
              <div className="mb-6">
                <p className="text-xs text-slate-400 mb-3 text-center">Session Mode</p>
                <div className="flex justify-center gap-3">
                  <Button 
                    onClick={() => setMode("only_listen")} 
                    variant={sessionMode === "only_listen" ? "default" : "outline"}
                    className={sessionMode === "only_listen" ? "bg-cyan-500 hover:bg-cyan-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}
                  >
                    <Mic className="w-4 h-4 mr-2" /> Only Listen
                  </Button>
                  <Button 
                    onClick={() => setMode("only_speak")} 
                    variant={sessionMode === "only_speak" ? "default" : "outline"}
                    className={sessionMode === "only_speak" ? "bg-purple-500 hover:bg-purple-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}
                  >
                    <Volume2 className="w-4 h-4 mr-2" /> Only Speak
                  </Button>
                  <Button 
                    onClick={() => setMode("listen_and_speak")} 
                    variant={sessionMode === "listen_and_speak" ? "default" : "outline"}
                    className={sessionMode === "listen_and_speak" ? `bg-gradient-to-r ${persona.color} text-white` : "border-slate-600 text-slate-300 hover:bg-slate-800"}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" /> Listen & Speak
                  </Button>
                </div>
              </div>
            )}

            {/* Suggested Questions - Show in Listen Mode */}
            {connectionStatus === "connected" && sessionMode === "only_listen" && (
              <div className="border-t border-slate-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-400 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Suggested Questions
                    {generatingQuestions && <Loader2 className="w-3 h-3 animate-spin text-cyan-400" />}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={generateQuestions}
                    disabled={generatingQuestions || !conversationContext.trim()}
                    className="text-cyan-400 hover:text-cyan-300 text-xs"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${generatingQuestions ? 'animate-spin' : ''}`} />
                    Generate Now
                  </Button>
                </div>
                
                {suggestedQuestions.length === 0 ? (
                  <p className="text-sm text-slate-500 italic text-center py-4">
                    {conversationContext.trim() 
                      ? "Click 'Generate Now' or wait for automatic suggestions..." 
                      : "Listening... Questions will appear as the conversation progresses."}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {suggestedQuestions.map((question, idx) => (
                      <div 
                        key={idx} 
                        className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 hover:bg-slate-800 transition-colors cursor-pointer"
                        onClick={() => {
                          // Copy question or use it
                          navigator.clipboard.writeText(question);
                          toast.success("Question copied to clipboard");
                        }}
                      >
                        <p className="text-sm text-slate-200">{question}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Quick Prompts */}
            {connectionStatus === "connected" && (sessionMode === "only_speak" || sessionMode === "listen_and_speak") && (
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs text-slate-400 mb-3 text-center">Quick Prompts</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {persona.quickPrompts.map((prompt) => (
                    <QuickPromptButton 
                      key={prompt.type}
                      icon={prompt.icon} 
                      label={prompt.label} 
                      onClick={() => sendQuickPrompt(prompt.type)} 
                      disabled={connectionStatus !== "connected"} 
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Extended Listening Mode - Only for Max Returns and Alex Chen */}
        {supportsExtendedListening && (
          <Card className="bg-slate-900/50 border-slate-800 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-amber-400" />
                Extended Listening Mode
              </CardTitle>
              <p className="text-sm text-slate-400">
                For long presentations - record passively, then generate thoughtful questions
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Follow-up Session Active - Listening for Answer */}
              {connectionStatus === "connected" && extendedQuestions.length > 0 && (
                <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${persona.color} overflow-hidden ${isSpeaking ? 'animate-pulse' : ''}`}>
                        <img src={persona.image} alt={persona.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-emerald-300">
                          {isSpeaking ? "Responding..." : isListening ? "Listening to answer..." : "Ready for conversation"}
                        </p>
                        <p className="text-xs text-slate-400">
                          {isSpeaking ? "Giving witty acknowledgment" : "Speak your answer"}
                        </p>
                      </div>
                    </div>
                    {(isSpeaking || isListening) && (
                      <div className="flex gap-1">
                        {[...Array(4)].map((_, i) => (
                          <div 
                            key={i} 
                            className={`w-1.5 rounded-full ${isSpeaking ? 'bg-purple-400 h-6' : 'bg-cyan-400 h-4'} animate-pulse`} 
                            style={{ animationDelay: `${i * 0.1}s` }} 
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={endSession} 
                    variant="outline" 
                    size="sm"
                    className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    End Follow-up Session
                  </Button>
                </div>
              )}

              {/* Recording Controls - Only show when no active session */}
              {connectionStatus !== "connected" && (
                <div className="space-y-4">
                  {/* Context Inputs - Only for Max Returns */}
                  {personaId === "max-returns" && !isRecording && extendedQuestions.length === 0 && !processingExtended && (
                    <div className="space-y-3 bg-slate-800/30 rounded-lg p-4">
                      <p className="text-xs text-slate-500 mb-2">Optional context for better questions:</p>
                      
                      {/* Presenter Name */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-slate-400 w-24">Presenter:</label>
                        <input
                          type="text"
                          value={presenterName}
                          onChange={(e) => setPresenterName(e.target.value)}
                          placeholder="Name of the presenter"
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
                          data-testid="presenter-name-input"
                        />
                      </div>
                      
                      {/* Company Name */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-slate-400 w-24">Company:</label>
                        <input
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Company being pitched"
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
                          data-testid="company-name-input"
                        />
                      </div>
                      
                      {/* Company Website */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-slate-400 w-24">Website:</label>
                        <input
                          type="url"
                          value={companyWebsite}
                          onChange={(e) => setCompanyWebsite(e.target.value)}
                          placeholder="https://company.com"
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
                          data-testid="company-website-input"
                        />
                      </div>
                      
                      {/* Voice Selection with Preview */}
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-slate-400 w-24">AI Voice:</label>
                        <select
                          value={questionVoice}
                          onChange={(e) => setQuestionVoice(e.target.value)}
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500"
                          data-testid="voice-select"
                        >
                          {VOICE_OPTIONS.map((voice) => (
                            <option key={voice.id} value={voice.id}>
                              {voice.name} - {voice.description}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={previewVoice}
                          disabled={previewingVoice}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700 px-3"
                          data-testid="voice-preview-btn"
                        >
                          {previewingVoice ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Volume2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-4">
                    {!isRecording && extendedQuestions.length === 0 && !processingExtended && (
                      <Button 
                        onClick={startExtendedRecording}
                        className="bg-amber-600 hover:bg-amber-700 px-6"
                      >
                        <CircleDot className="w-4 h-4 mr-2" />
                        Long Listen
                      </Button>
                    )}
                    
                    {isRecording && (
                      <>
                        <div className="flex items-center gap-3 bg-red-500/20 px-4 py-2 rounded-lg">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-red-400 font-mono">{formatDuration(recordingDuration)}</span>
                          {extendedQuestions.length > 0 && (
                            <span className="text-slate-400 text-xs">({extendedQuestions.length} questions)</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {/* Generate Questions - keeps recording */}
                          <Button 
                            onClick={() => generateExtendedQuestions(false)}
                            disabled={processingExtended}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            title="Generate questions and keep recording"
                          >
                            {processingExtended ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                            ) : (
                              <><Brain className="w-4 h-4 mr-2" /> Generate</>
                            )}
                          </Button>
                          
                          {/* Generate & Stop */}
                          <Button 
                            onClick={() => generateExtendedQuestions(true)}
                            disabled={processingExtended}
                            className="bg-amber-600 hover:bg-amber-700"
                            title="Generate final questions and stop recording"
                          >
                            <Brain className="w-4 h-4 mr-2" />
                            Generate & Stop
                          </Button>
                          
                          {/* Just Stop */}
                          {extendedQuestions.length > 0 && (
                            <Button 
                              onClick={justStopRecording}
                              disabled={processingExtended}
                              variant="outline"
                              className="border-slate-600 text-slate-300"
                              title="Stop recording without generating more questions"
                            >
                              <Square className="w-4 h-4 mr-2" />
                              Stop
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                    
                    {processingExtended && !isRecording && (
                      <div className="flex items-center gap-2 text-amber-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Transcribing and generating questions...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Generated Questions - Show when we have questions (even while recording) */}
              {extendedQuestions.length > 0 && connectionStatus !== "connected" && (
                <div className="space-y-3 pt-4 border-t border-slate-800">
                  {/* Recording indicator if still recording */}
                  {isRecording && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
                      <p className="text-xs text-amber-400 flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        Recording continues... Click questions to preview (won't start voice session until recording stops)
                      </p>
                    </div>
                  )}
                  
                  {/* Speaker Summary */}
                  {extendedTranscript?.speakers && Object.keys(extendedTranscript.speakers).length > 0 && (
                    <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
                      <p className="text-xs text-slate-400 mb-2 flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        Identified Speakers
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(extendedTranscript.speakers).map(([label, name]) => (
                          <span 
                            key={label}
                            className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-300"
                          >
                            {name || `Speaker ${label}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-slate-400 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Select a question to ask, or click edit to modify it first
                  </p>
                  <div className="space-y-2">
                    {extendedQuestions.map((question, idx) => (
                      <div
                        key={idx}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${
                          speakingQuestion === idx 
                            ? 'bg-amber-500/20 border-amber-500 text-amber-100' 
                            : editingQuestionIndex === idx
                            ? 'bg-slate-700/50 border-purple-500 text-slate-200'
                            : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-slate-600 text-slate-200'
                        } ${speakingQuestion !== null && speakingQuestion !== idx ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            speakingQuestion === idx ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-300'
                          }`}>
                            {speakingQuestion === idx ? (
                              <Volume2 className="w-4 h-4 animate-pulse" />
                            ) : (
                              idx + 1
                            )}
                          </div>
                          
                          {editingQuestionIndex === idx ? (
                            /* Editing mode */
                            <div className="flex-1 space-y-2">
                              <textarea
                                value={editingQuestionText}
                                onChange={(e) => setEditingQuestionText(e.target.value)}
                                className="w-full p-2 bg-slate-900 border border-slate-600 rounded text-sm text-white resize-none focus:outline-none focus:border-purple-500"
                                rows={3}
                                autoFocus
                              />
                              <div className="flex gap-2 justify-end">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={cancelEditingQuestion}
                                  className="border-slate-600 text-slate-400 h-7 px-2"
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={saveEditedQuestion}
                                  className="bg-purple-600 hover:bg-purple-700 h-7 px-2"
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* Display mode */
                            <div className="flex-1 flex items-start justify-between gap-2">
                              <button 
                                onClick={() => speakQuestion(question, idx)}
                                disabled={speakingQuestion !== null && speakingQuestion !== idx}
                                className="flex-1 text-left"
                              >
                                <p className="text-sm leading-relaxed">{question}</p>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingQuestion(idx);
                                }}
                                disabled={speakingQuestion !== null}
                                className="flex-shrink-0 p-1.5 text-slate-400 hover:text-purple-400 hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                                title="Edit question"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Reset button */}
                  <div className="flex justify-center pt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setExtendedQuestions([]);
                        setExtendedTranscript(null);
                        setRecordingDuration(0);
                        setPresenterName("");
                        setCompanyName("");
                        setCompanyWebsite("");
                        setAskedQuestions([]);
                        setExtendedTranscriptText("");
                        accumulatedTranscriptRef.current = "";
                      }}
                      className="border-slate-700 text-slate-400"
                    >
                      <RefreshCw className="w-3 h-3 mr-2" />
                      Start New Recording
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              {persona.name} Settings
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="personality" className="mt-4">
            <TabsList className="bg-slate-800 border-slate-700">
              <TabsTrigger value="personality">Personality</TabsTrigger>
              <TabsTrigger value="knowledge">Knowledge Base</TabsTrigger>
            </TabsList>

            <TabsContent value="personality" className="space-y-4 mt-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-5">
                  {/* Voice Selection */}
                  <div className="pb-4 border-b border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <AudioLines className="w-4 h-4 text-purple-400" />
                      <Label className="text-slate-200 font-medium">Voice</Label>
                    </div>
                    <Select value={settings?.voice || "verse"} onValueChange={v => updateSettings({ voice: v })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alloy">Alloy - Neutral & balanced</SelectItem>
                        <SelectItem value="ash">Ash - Soft & warm</SelectItem>
                        <SelectItem value="ballad">Ballad - Warm & engaging</SelectItem>
                        <SelectItem value="coral">Coral - Clear & expressive</SelectItem>
                        <SelectItem value="echo">Echo - Smooth & resonant</SelectItem>
                        <SelectItem value="sage">Sage - Calm & authoritative</SelectItem>
                        <SelectItem value="shimmer">Shimmer - Bright & lively</SelectItem>
                        <SelectItem value="verse">Verse - Confident & articulate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Question Generation Interval */}
                  <div className="pb-4 border-b border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-cyan-400" />
                      <Label className="text-slate-200 font-medium">Question Generation (Listen Mode)</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input 
                        type="number" 
                        min="1" 
                        max="10"
                        value={questionInterval} 
                        onChange={e => setQuestionInterval(parseInt(e.target.value) || 2)}
                        className="bg-slate-900 border-slate-700 text-white w-20"
                      />
                      <span className="text-slate-400 text-sm">minutes between question suggestions</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">When in &quot;Only Listen&quot; mode, AI will suggest questions based on what it hears.</p>
                  </div>
                  
                  {/* Personality Sliders */}
                  {[
                    { key: "directness", label: "Directness", left: "Diplomatic", right: "Blunt" },
                    { key: "humor_level", label: "Humor", left: "Serious", right: "Witty" },
                    { key: "technical_depth", label: "Technical Depth", left: "High-level", right: "Deep dive" },
                    { key: "encouragement_vs_challenge", label: "Feedback Style", left: "Encouraging", right: "Challenging" },
                  ].map(({ key, label, left, right }) => (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-400">{left}</span>
                        <span className="text-slate-200 font-medium">{label}</span>
                        <span className="text-slate-400">{right}</span>
                      </div>
                      <Slider value={[settings?.personality?.[key] || 50]} onValueChange={([v]) => updatePersonality(key, v)} max={100} step={5} className="w-full" />
                    </div>
                  ))}
                  
                  <div className="pt-4">
                    <Label className="text-slate-200">Custom Instructions</Label>
                    <Textarea value={settings?.personality?.custom_instructions || ""} onChange={e => updatePersonality("custom_instructions", e.target.value)} className="bg-slate-900 border-slate-700 text-white mt-2" placeholder="Additional personality details..." rows={3} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Knowledge Base Tab */}
            <TabsContent value="knowledge" className="space-y-4 mt-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-4">
                  <p className="text-sm text-slate-400">
                    Add documents or URLs to inform {persona.name}&apos;s knowledge for this conversation.
                  </p>
                  
                  {/* Add URL */}
                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2">
                      <Link className="w-4 h-4" /> Add URL
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        value={newUrl}
                        onChange={e => setNewUrl(e.target.value)}
                        placeholder="https://example.com/article"
                        className="bg-slate-900 border-slate-700 text-white flex-1"
                      />
                      <Button onClick={addKnowledgeUrl} size="sm" className="bg-cyan-600 hover:bg-cyan-700">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Upload Document */}
                  <div className="space-y-2">
                    <Label className="text-slate-200 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Upload Document
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        type="file"
                        accept=".pdf,.txt,.doc,.docx"
                        onChange={handleDocUpload}
                        disabled={uploadingDoc}
                        className="bg-slate-900 border-slate-700 text-white flex-1"
                      />
                      {uploadingDoc && <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />}
                    </div>
                  </div>
                  
                  {/* Knowledge Base Items */}
                  <div className="space-y-2 pt-4 border-t border-slate-700">
                    <Label className="text-slate-200">Current Knowledge ({(settings?.knowledge_base || []).length} items)</Label>
                    {(settings?.knowledge_base || []).length === 0 ? (
                      <p className="text-sm text-slate-500 italic">No items in knowledge base yet.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {(settings?.knowledge_base || []).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-slate-900/50 rounded p-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {item.type === "url" ? <Link className="w-4 h-4 text-cyan-400 flex-shrink-0" /> : <FileText className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                              <span className="text-sm text-slate-300 truncate">
                                {item.type === "url" ? item.content : item.name}
                              </span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeKnowledgeItem(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Private Briefing Modal */}
      <Dialog open={showPrivateBriefing} onOpenChange={setShowPrivateBriefing}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Private Briefing with {persona.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Before the session starts, give {persona.name} specific instructions on how to behave.
            </p>
            
            <Textarea 
              value={privateBriefing} 
              onChange={e => setPrivateBriefing(e.target.value)} 
              className="bg-slate-800 border-slate-700 text-white min-h-[150px]" 
              placeholder={`e.g., Focus on asking about their competitive landscape... Be extra encouraging... Challenge their assumptions...`}
            />
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setPrivateBriefing(""); startSession(); }} className="flex-1 border-slate-700 text-slate-300">
                Skip Briefing
              </Button>
              <Button onClick={startSession} disabled={connectionStatus === "connecting"} className={`flex-1 bg-gradient-to-r ${persona.color}`}>
                {connectionStatus === "connecting" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : <><Play className="w-4 h-4 mr-2" /> Start Session</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
